import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';

const roundMoney = (n) => Math.round(n * 100) / 100;

/** Payment statuses that release their allocation — must mirror server `DEAD_STATUSES`. */
const DEAD_PAYMENT_STATUSES = ['FAILED', 'EXPIRED', 'CANCELLED'];

/**
 * Matches server `isValidPaymentOnPaymentItems`: sum of map rows for these payment items,
 * excluding mappings whose payment is dead (failed/expired/cancelled) so an expired link's
 * amount is unlocked for a fresh link.
 */
const sumAllocatedMappedAmounts = (item) => {
    const mappings = item?.paymentMappings;
    if (!Array.isArray(mappings) || mappings.length === 0) return 0;
    return mappings
        .filter((m) => !DEAD_PAYMENT_STATUSES.includes(m?.payment?.status))
        .reduce((sum, m) => sum + (parseFloat(m?.amount) || 0), 0);
};

const validatePaymentAmount = (amountInput, maxAmount) => {
    const raw = amountInput.trim().replace(/,/g, '');
    if (raw === '') {
        return { ok: false, amount: null, message: 'Enter a valid amount.' };
    }
    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) {
        return { ok: false, amount: null, message: 'Enter a valid amount.' };
    }
    const amount = roundMoney(parsed);
    const maxRounded = roundMoney(maxAmount);
    if (amount <= 0) {
        return { ok: false, amount, message: 'Amount must be greater than zero.' };
    }
    if (amount > maxRounded) {
        return {
            ok: false,
            amount,
            message: `Amount cannot exceed ₹ ${maxRounded.toFixed(2)} (remaining after amounts already on payment links).`,
        };
    }
    return { ok: true, amount, message: null };
};

const MapUserPatientSummary = ({ mapUserPatient }) => {
    const patient = mapUserPatient?.Patient;
    const user = mapUserPatient?.User;
    if (!patient && !user) {
        return <span className="text-gray-500">No user mapping available</span>;
    }
    const showUserLine = user && patient && (user.id !== patient.id || user.name !== patient.name);
    const relationship =
        mapUserPatient?.relationship && mapUserPatient.relationship !== 'OLD'
            ? String(mapUserPatient.relationship).toLowerCase()
            : null;

    return (
        <div className="mt-1 space-y-0.5 text-gray-700">
            {patient && (
                <div>
                    <span className="text-gray-500">Patient:</span> {patient.name}
                    {patient.contact_no ? ` · ${patient.contact_no}` : ''}
                </div>
            )}
            {showUserLine && (
                <div>
                    <span className="text-gray-500">User{relationship ? ` (${relationship})` : ''}:</span> {user.name}
                    {user.contact_no ? ` · ${user.contact_no}` : ''}
                </div>
            )}
            {!patient && user && (
                <div>
                    <span className="text-gray-500">User:</span> {user.name}
                    {user.contact_no ? ` · ${user.contact_no}` : ''}
                </div>
            )}
        </div>
    );
};

MapUserPatientSummary.propTypes = {
    mapUserPatient: PropTypes.object,
};

const CreatePaymentLinkModal = ({ selectedItems, orderData, involvedQuotesMapUsers = [], onClose, onCreate }) => {
    const [paymentMode, setPaymentMode] = useState('PAYU');
    const [debitCoinWallet, setDebitCoinWallet] = useState(true);
    const { maxPayableAmount, itemsLineTotal, allocatedTotal } = useMemo(() => {
        const itemsLineTotal = selectedItems.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0);
        const allocatedTotal = selectedItems.reduce((sum, item) => sum + sumAllocatedMappedAmounts(item), 0);
        const maxPayableAmount = roundMoney(Math.max(0, itemsLineTotal - allocatedTotal));
        return { maxPayableAmount, itemsLineTotal, allocatedTotal };
    }, [selectedItems]);
    const [isCreating, setIsCreating] = useState(false);
    const [recipientName, setRecipientName] = useState('');
    const [recipientPhone, setRecipientPhone] = useState('');
    const [amountInput, setAmountInput] = useState('');

    const user = orderData?.mapUserPatient?.Patient;
    const userId = user?.id;
    const userNameFromRecord = user?.name || '';
    const userPhoneFromRecord = user?.contact_no || '';

    useEffect(() => {
        setRecipientName(userNameFromRecord);
        setRecipientPhone(userPhoneFromRecord);
    }, [userNameFromRecord, userPhoneFromRecord, userId]);

    useEffect(() => {
        setAmountInput(maxPayableAmount.toFixed(2));
    }, [maxPayableAmount]);

    const trimmedRecipientName = recipientName.trim();
    const trimmedRecipientPhone = recipientPhone.trim();
    const hasUserMapping = !!userId;
    const payuRecipientReady =
        trimmedRecipientName.length > 0 && trimmedRecipientPhone.length > 0;
    const amountValidation = useMemo(
        () => validatePaymentAmount(amountInput, maxPayableAmount),
        [amountInput, maxPayableAmount],
    );
    const isPayuMode = paymentMode === 'PAYU' || paymentMode === 'PAYU_OPEN_LINK';
    const canCreate =
        hasUserMapping &&
        amountValidation.ok &&
        (paymentMode === 'COD' || paymentMode === 'QR' || paymentMode === 'B2B' || paymentMode === 'IN_APP' || paymentMode === 'COINS' || (isPayuMode && payuRecipientReady));

    const handleCreate = async () => {
        if (!canCreate) {
            return;
        }
        setIsCreating(true);
        try {
            const base = {
                mode: paymentMode,
                paymentItemIds: selectedItems.map((item) => parseInt(item.id, 10)),
                userId,
                amount: amountValidation.amount,
                currency: 'INR',
            };
            const success = await onCreate(
                paymentMode === 'COD' || paymentMode === 'QR' || paymentMode === 'B2B' || paymentMode === 'IN_APP'
                    ? base
                    : paymentMode === 'COINS'
                        ? { ...base, debitWallet: debitCoinWallet }
                        : {
                            ...base,
                            gateway: 'PAYU',
                            user: {
                                name: trimmedRecipientName,
                                phone: trimmedRecipientPhone,
                            },
                        },
            );
            if (success) onClose();
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Modal title="Collect payment" onClose={onClose}>
            <div className="space-y-4">
                <div>
                    <label htmlFor="payment-collection-mode" className="text-sm font-medium text-gray-700 mb-2 block">
                        Payment method
                    </label>
                    <select
                        id="payment-collection-mode"
                        value={paymentMode}
                        onChange={(e) => setPaymentMode(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 font-sans text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="PAYU">PayU payment link</option>
                        <option value="PAYU_OPEN_LINK">PayU open link</option>
                        <option value="COD">Cash on delivery (COD)</option>
                        <option value="QR">QR (manual payment received)</option>
                        <option value="B2B">B2B (paid)</option>
                        <option value="IN_APP">In-app (missed)</option>
                        <option value="COINS">Coins (missed)</option>
                    </select>
                    {paymentMode === 'PAYU_OPEN_LINK' && (
                        <p className="mt-2 text-xs text-gray-500">
                            Creates a <strong>pending</strong> PayU payment link with method <code>OPEN_LINK</code>, source <code>PAYMENT_LINK</code>,
                            and gateway <code>PAYU</code>. Use when the customer will pay via a generic open payment link rather than UPI.
                        </p>
                    )}
                    {paymentMode === 'COD' && (
                        <p className="mt-2 text-xs text-gray-500">
                            Creates a <strong>pending</strong> payment with method cash on delivery and reserves this amount on the
                            selected payment items (same idea as a pending PayU link). When the customer pays, mark the payment successful in
                            admin. No payment link is sent.
                        </p>
                    )}
                    {paymentMode === 'QR' && (
                        <p className="mt-2 text-xs text-gray-500">
                            Records a <strong>successful</strong> QR payment immediately — the customer already paid on the office QR code.
                            Items are marked PAID right away. No payment link is sent.
                        </p>
                    )}
                    {paymentMode === 'B2B' && (
                        <p className="mt-2 text-xs text-gray-500">
                            Records a <strong>successful</strong> B2B payment immediately — the amount was settled via the B2B channel.
                            Items are marked PAID right away. No payment link is sent.
                        </p>
                    )}
                    {paymentMode === 'IN_APP' && (
                        <p className="mt-2 text-xs text-gray-500">
                            Manually records a <strong>successful</strong> in-app payment that the app failed to save. Mirrors the app
                            fields (source IN_APP, gateway PayU, method UPI). Items are marked PAID right away.
                        </p>
                    )}
                    {paymentMode === 'COINS' && (
                        <div className="mt-2 space-y-2">
                            <p className="text-xs text-gray-500">
                                Manually records a <strong>successful</strong> coin-redemption payment that the app failed to save
                                (source IN_APP, gateway COIN, method COIN_REDEEM). Items are marked PAID right away.
                            </p>
                            <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={debitCoinWallet}
                                    onChange={(e) => setDebitCoinWallet(e.target.checked)}
                                    className="h-4 w-4 border-gray-300 text-blue-600"
                                />
                                <span>Also debit the customer&apos;s coin wallet (only if coins were NOT already deducted in-app)</span>
                            </label>
                        </div>
                    )}
                </div>
                {involvedQuotesMapUsers.length > 0 && (
                    <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">Users by quotation</div>
                        <div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3">
                            {involvedQuotesMapUsers.map((row) => (
                                <div key={row.rowKey} className="border-b border-gray-200 pb-2 last:border-b-0 last:pb-0">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {row.quotationId != null ? `Quote #${row.quotationId}` : `Order #${row.orderId ?? '-'}`}
                                    </div>
                                    <MapUserPatientSummary mapUserPatient={row.mapUserPatient} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {isPayuMode && (
                <div className="text-sm text-gray-600">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Payment link recipient</p>
                    <label htmlFor="create-payment-link-recipient-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Recipient name
                    </label>
                    <input
                        id="create-payment-link-recipient-name"
                        type="text"
                        autoComplete="name"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder={userNameFromRecord || 'Name'}
                        className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 font-sans text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <label htmlFor="create-payment-link-recipient-phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Recipient phone
                    </label>
                    <input
                        id="create-payment-link-recipient-phone"
                        type="text"
                        inputMode="tel"
                        autoComplete="tel"
                        value={recipientPhone}
                        onChange={(e) => setRecipientPhone(e.target.value)}
                        placeholder={userPhoneFromRecord || 'Phone number'}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 font-sans text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    {!hasUserMapping && (
                        <p className="text-amber-600 mt-2">A mapped user is required to create a payment link.</p>
                    )}
                    {hasUserMapping && !trimmedRecipientName && (
                        <p className="text-amber-600 mt-2">Enter the recipient name for the payment link.</p>
                    )}
                    {hasUserMapping && trimmedRecipientName && !trimmedRecipientPhone && (
                        <p className="text-amber-600 mt-2">Enter the phone number that should receive the payment link.</p>
                    )}
                </div>
                )}
                {paymentMode === 'COD' && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                        {!hasUserMapping ? (
                            <p className="text-amber-600">A mapped user is required to create a pending COD payment.</p>
                        ) : (
                            <p>
                                Payment will be created as <strong>pending</strong> for user ID{' '}
                                <span className="font-mono text-gray-800">{userId}</span>
                                {userNameFromRecord ? ` (${userNameFromRecord})` : ''}. Mark it paid when cash is received.
                            </p>
                        )}
                    </div>
                )}
                <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Selected Payment Items</div>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left">ID</th>
                                    <th className="px-3 py-2 text-left">Invoice Type</th>
                                    <th className="px-3 py-2 text-right">Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedItems.map((item) => (
                                    <tr key={item.id} className="border-t border-gray-100">
                                        <td className="px-3 py-2">{item.id}</td>
                                        <td className="px-3 py-2">{item.invoiceType || '-'}</td>
                                        <td className="px-3 py-2 text-right font-medium">{item.totalAmount ?? '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="border-t border-gray-200 py-3 space-y-2">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <label htmlFor="create-payment-link-amount" className="text-sm font-semibold text-gray-800">
                            Payment amount
                        </label>
                        <div className="text-right text-xs text-gray-500">
                            <div>Maximum ₹ {maxPayableAmount.toFixed(2)} remaining</div>
                            {allocatedTotal > 0 && (
                                <div className="mt-0.5 text-gray-400">
                                    Items ₹ {roundMoney(itemsLineTotal).toFixed(2)} − already on links ₹{' '}
                                    {roundMoney(allocatedTotal).toFixed(2)}
                                </div>
                            )}
                        </div>
                    </div>
                    <input
                        id="create-payment-link-amount"
                        type="text"
                        inputMode="decimal"
                        value={amountInput}
                        onChange={(e) => setAmountInput(e.target.value)}
                        className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 font-sans text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    {!amountValidation.ok && amountValidation.message && (
                        <p className="text-sm text-amber-600">{amountValidation.message}</p>
                    )}
                </div>
                <div className="flex justify-end gap-3 pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={isCreating || selectedItems.length === 0 || !canCreate}
                        className={`px-4 py-2 text-white-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            paymentMode === 'COD'
                                ? 'bg-emerald-600 hover:bg-emerald-700'
                                : paymentMode === 'QR'
                                  ? 'bg-teal-600 hover:bg-teal-700'
                                  : paymentMode === 'B2B'
                                    ? 'bg-purple-600 hover:bg-purple-700'
                                    : paymentMode === 'IN_APP'
                                      ? 'bg-indigo-600 hover:bg-indigo-700'
                                      : paymentMode === 'COINS'
                                        ? 'bg-amber-600 hover:bg-amber-700'
                                        : paymentMode === 'PAYU_OPEN_LINK'
                                          ? 'bg-cyan-600 hover:bg-cyan-700'
                                          : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                    >
                        {isCreating
                            ? 'Working...'
                            : paymentMode === 'COD'
                              ? 'Create pending COD payment'
                              : paymentMode === 'QR'
                                ? 'Record QR payment'
                                : paymentMode === 'B2B'
                                  ? 'Record B2B payment'
                                  : paymentMode === 'IN_APP'
                                    ? 'Record in-app payment'
                                    : paymentMode === 'COINS'
                                      ? 'Record coin payment'
                                      : paymentMode === 'PAYU_OPEN_LINK'
                                        ? 'Create open link'
                                        : 'Create payment link'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

CreatePaymentLinkModal.propTypes = {
    selectedItems: PropTypes.array.isRequired,
    orderData: PropTypes.object,
    involvedQuotesMapUsers: PropTypes.arrayOf(
        PropTypes.shape({
            rowKey: PropTypes.string.isRequired,
            quotationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            mapUserPatient: PropTypes.object,
        }),
    ),
    onClose: PropTypes.func.isRequired,
    onCreate: PropTypes.func.isRequired,
};

export default CreatePaymentLinkModal;
